import * as React from 'react'

import {
  Box,
  Chevron,
  CollapsableCard,
  Icon,
  List,
  MediumIcon,
  Tag,
  Text,
  useToggle,
} from '@factorialco/deprecated-design-system'
import { BookkeeperCompanyIncidenceOptionFragment } from 'generated/resources/graphql'

import hasFeature from 'modules/core/lib/hasFeature'
import i18n from 'modules/core/lib/i18n'

import { isIncidenceActiveForCompany } from 'modules/bookkeepers/lib/incidenceOptions'
import { IncidenceOptionsType } from 'modules/bookkeepers/lib/incidenceOptions'
import { incidenceNeedsCompanyValidation } from 'modules/bookkeepers/lib/incidenceOptions'

import { Item } from './_Item'

interface Props {
  incidenceOptions: IncidenceOptionsType[]
  title: string
  icon: MediumIcon
  sectionReference: React.Ref<HTMLElement>
  companyIncidenceSettings: BookkeeperCompanyIncidenceOptionFragment[]
  onTogglerChange: (type: IncidenceOptionsType, isValidation?: boolean) => void
  onLoadingIncidenceOption: boolean
}

export const IncidenceOptionsSection = ({
  incidenceOptions,
  title,
  icon,
  sectionReference,
  companyIncidenceSettings,
  onTogglerChange,
  onLoadingIncidenceOption,
}: Props) => {
  const hasProfessionals = hasFeature('factorial_professionals')
  const getDescription = (incidenceOption: IncidenceOptionsType) => {
    const activated = isIncidenceActiveForCompany(companyIncidenceSettings, incidenceOption)
    const trackedText = activated
      ? i18n.t('app_store_v2.apps.payroll_bundle.settings.incidences.tracked')
      : i18n.t('app_store_v2.apps.payroll_bundle.settings.incidences.not_tracked')
    let needsValidationText = ''
    if (activated) {
      needsValidationText = incidenceNeedsCompanyValidation(
        companyIncidenceSettings,
        incidenceOption
      )
        ? i18n.t('app_store_v2.apps.payroll_bundle.settings.incidences.needs_company_validation')
        : i18n.t(
            'app_store_v2.apps.payroll_bundle.settings.incidences.doesnt_need_company_validation'
          )
    }

    return `${trackedText} ${needsValidationText}`
  }

  const [isContentOpen, toggleContentVisibility] = useToggle(false)
  const chevronDirection = isContentOpen ? 'up' : 'down'
  const countActiveOptions = incidenceOptions.filter((incidenceOption) =>
    isIncidenceActiveForCompany(companyIncidenceSettings, incidenceOption)
  ).length

  const [loadingIncidenceOption, setLoadingIncidenceOption] = React.useState<string | null>(null)

  const handleTogglerChange = (incidenceOption: IncidenceOptionsType) => {
    setLoadingIncidenceOption(incidenceOption.id)
    onTogglerChange(incidenceOption)
  }

  return (
    <Box width="full" gap="s16" ref={sectionReference}>
      <CollapsableCard
        open={isContentOpen}
        Card={
          <Box
            flexDirection="row"
            justifyContent="spaceBetween"
            onClick={toggleContentVisibility(!isContentOpen)}
          >
            <Box gap="s8" flexDirection="row">
              <Icon.Medium icon={icon}></Icon.Medium>
              <Text.Normal>{title}</Text.Normal>
              <Tag type="grey">{incidenceOptions.length}</Tag>
            </Box>
            <Box flexDirection="row" alignItems="center" gap="s8">
              <Tag type={countActiveOptions ? 'viridian' : 'grey'}>
                {i18n.tp('app_store_v2.apps.payroll_bundle.settings.incidences.enabled', {
                  count: countActiveOptions,
                })}
              </Tag>
              <Chevron size="big" type="terciary" direction={chevronDirection} />
            </Box>
          </Box>
        }
        Content={
          <List>
            {incidenceOptions.map((incidenceOption) => (
              <Item
                key={incidenceOption.id + incidenceOption.name}
                title={incidenceOption.getTranslatedTitle()}
                description={getDescription(incidenceOption)}
                onTogglerChange={() => handleTogglerChange(incidenceOption)}
                disabled={
                  (loadingIncidenceOption === incidenceOption.id && onLoadingIncidenceOption) ||
                  hasProfessionals ||
                  incidenceOption.disabled
                }
                active={isIncidenceActiveForCompany(companyIncidenceSettings, incidenceOption)}
              />
            ))}
          </List>
        }
      />
    </Box>
  )
}
